import { Link, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { BRAND_LOGO_URL, BROCHURE_URL } from '@/data/servicesData';

const NAV_LINKS = [
  { id: 'services', to: '/services', label: 'Services', testId: 'nav-services-link' },
  { id: 'about', to: '/about', label: 'About', testId: 'nav-about-link' },
  { id: 'contact', to: '/contact', label: 'Get Quote', testId: 'nav-contact-link' },
];

const getDesktopLinkClass = ({ isActive, isContact }) => {
  if (isContact) {
    return `px-4 lg:px-6 py-2 rounded-none splash-action font-mono text-sm lg:text-sm uppercase tracking-wider transition-all duration-300 ${
      isActive ? 'bg-white text-black' : 'bg-blackline-accent text-black hover:bg-white'
    }`;
  }

  return `px-3 lg:px-4 py-2 rounded-none splash-action border font-mono text-sm lg:text-sm uppercase tracking-wider transition-all duration-300 ${
    isActive ? 'border-blackline-accent text-blackline-accent' : 'border-blackline-border text-zinc-400 hover:text-white hover:border-white'
  }`;
};

const getMobileLinkClass = ({ isActive, isContact }) => {
  if (isContact) {
    return `block px-6 py-4 font-mono text-sm uppercase tracking-wider transition-all duration-300 rounded-none text-center splash-action ${
      isActive ? 'bg-white text-black' : 'bg-blackline-accent text-black hover:bg-white'
    }`;
  }

  return `block px-6 py-4 font-mono text-sm uppercase tracking-wider transition-all duration-300 rounded-none splash-action ${
    isActive ? 'bg-blackline-accent text-black' : 'text-zinc-200 hover:text-white hover:bg-blackline-surface border border-blackline-border'
  }`;
};

export default function Navigation() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 10);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  const navStyle = useMemo(() => (
    `fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
      isScrolled
        ? 'bg-blackline-bg/95 backdrop-blur-xl border-b border-blackline-border shadow-lg'
        : 'bg-transparent'
    } nav-splash-border`
  ), [isScrolled]);

  const handleServicesClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  }, []);

  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const handleMobileLinkClick = useCallback((path) => {
    if (path === '/services') {
      handleServicesClick();
      return;
    }

    setIsMenuOpen(false);
  }, [handleServicesClick]);

  return (
    <nav
      className={navStyle}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1 sm:py-1">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-5">
            <Link to="/" className="flex items-center logo-container" data-testid="logo-link">
              <div className="logo-frame">
                <img 
                  src={BRAND_LOGO_URL} 
                  alt="BLACKLINE CLEANING" 
                  className="logo-blend h-40 sm:h-44 md:h-48 lg:h-56 w-auto object-contain transition-all duration-300"
                />
              </div>
            </Link>
            <a
              href={BROCHURE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-top-left-brochure-link"
              className="inline-block px-3 lg:px-4 py-2 rounded-none splash-action border border-blackline-border font-mono text-[11px] sm:text-sm uppercase tracking-wider text-zinc-300 hover:text-white hover:border-white transition-all duration-300"
            >
              Brochure
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3 lg:gap-6">
            {NAV_LINKS.map((link) => {
              const linkIsActive = link.id === 'services'
                ? isActive('/services') || isActive('/')
                : isActive(link.to);
              const isContact = link.id === 'contact';

              return (
                <Link
                  key={link.id}
                  to={link.to}
                  onClick={link.id === 'services' ? handleServicesClick : undefined}
                  data-testid={link.testId}
                  className={getDesktopLinkClass({ isActive: linkIsActive, isContact })}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={handleMenuToggle}
            className="md:hidden text-white p-2 z-50 relative splash-action"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation - SOLID BACKGROUND */}
        {isMenuOpen && (
          <div className="md:hidden absolute left-0 right-0 top-full mobile-menu-dropdown animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-3">
              {NAV_LINKS.map((link) => {
                const linkIsActive = link.id === 'services'
                  ? isActive('/services') || isActive('/')
                  : isActive(link.to);
                const isContact = link.id === 'contact';

                return (
                  <Link
                    key={`mobile-${link.id}`}
                    to={link.to}
                    onClick={() => handleMobileLinkClick(link.to)}
                    className={getMobileLinkClass({ isActive: linkIsActive, isContact })}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}